import { Link } from "react-router-dom";
import { ArrowUpRight, Award, Globe, Leaf, Hammer, ChevronDown, PenTool, Scissors, Settings, Sparkles, Wrench, ClipboardCheck, Truck } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, RevealStagger, RevealItem } from "@/components/Reveal";
import { CountUp } from "@/components/CountUp";
import { BRAND, IMAGES, STATS } from "@/lib/site";

const VALUES = [
  { icon: Hammer, title: "Engineering First", body: "Every product begins as a stress-tested CAD model, not a sketch." },
  { icon: Award, title: "Uncompromising Quality", body: "27-point QC, certified welds, premium powder coating." },
  { icon: Globe, title: "Global Mindset", body: "Built in India, shipping to showrooms in 15+ countries." },
  { icon: Leaf, title: "Sustainable Craft", body: "Recyclable steel, low-VOC powder coating, zero-waste fabrication." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="About Stenzia"
        title="Engineered in Morbi. Trusted across the world."
        subtitle="Stenzia Displays LLP is an Indian manufacturer of premium tile display systems for showrooms, exporters and architects."
        image={IMAGES.AboutsUS}
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-stenzia-green"><span className="kbd-line" /> Our Story</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl tracking-tighter text-charcoal leading-[1.05]">
              Born in the tile capital of India.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-charcoal/70 leading-relaxed">
            <p>Stenzia Displays was founded in Morbi — the world&apos;s second-largest ceramic tile manufacturing hub — by a team of engineers and showroom veterans who saw a gap: tile manufacturers were producing exceptional tiles, but presenting them on inadequate display systems.</p>
            <p>We didn&apos;t set out to build just another tile display stand. We set out to redefine how premium surfaces are presented, experienced and remembered — and that intent still shapes every decision inside our factory.</p>
            <p>Every Stenzia display system is engineered to maximise showroom efficiency, elevate customer experience and showcase every tile with precision, elegance and purpose. Load-tested, climate-tested, and finished to architect-grade specifications, our systems live in over 500 showrooms across India, the Middle East, Europe, Africa and South-East Asia.</p>
            <p>The mission is simple — engineer display systems that elevate every tile they hold, and every brand that builds with them.</p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
        <div className="grain absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((s) => {
              const num = parseFloat(s.value.replace(/[^0-9.]/g, "")) || 0;
              const suffix = s.value.replace(/[0-9.]/g, "");
              return (
                <div key={s.label} className="border-l border-white/15 pl-5">
                  <div className="font-display text-4xl md:text-5xl tabular-nums">
                    <CountUp end={num} suffix={suffix} />
                  </div>
                  <div className="text-xs mt-2 text-white/55 tracking-wider uppercase">{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#F6F7F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-10 rounded-2xl border border-charcoal/10">
              <div className="overline text-stenzia-green">Vision</div>
              <p className="mt-4 font-display text-2xl md:text-3xl text-charcoal leading-snug tracking-tight">To set the global benchmark for tile display engineering.</p>
            </div>
            <div className="bg-charcoal text-white p-10 rounded-2xl">
              <div className="overline text-stenzia-gold">Mission</div>
              <p className="mt-4 font-display text-2xl md:text-3xl leading-snug tracking-tight">Engineer display systems that elevate every tile, every brand and every showroom they enter.</p>
            </div>
          </div>

          <div className="mt-16">
            <div className="overline text-stenzia-green"><span className="kbd-line" /> Our Values</div>
            <RevealStagger className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {VALUES.map((v) => (
                <RevealItem key={v.title}>
                  <div data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`} className="group h-full bg-white p-8 rounded-3xl border border-transparent hover:border-stenzia-green/20 hover:shadow-xl hover:-translate-y-1 transition-all duration-500">
                    <div className="w-14 h-14 rounded-2xl bg-stenzia-green text-white flex items-center justify-center group-hover:rotate-6 group-hover:scale-110 transition-all duration-500 shadow-lg shadow-stenzia-green/20"><v.icon size={20} /></div>
                    <h3 className="mt-5 font-display text-xl text-charcoal tracking-tight">{v.title}</h3>
                    <p className="mt-2 text-sm text-charcoal/65 leading-relaxed">{v.body}</p>
                  </div>
                </RevealItem>
              ))}
            </RevealStagger>
          </div>
        </div>
      </section>

      
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="overline text-stenzia-green">Partner with us</div>
          <h2 className="mt-6 font-display font-light text-4xl md:text-5xl tracking-tighter text-charcoal leading-[1.05]">Let&apos;s build your next showroom.</h2>
          <p className="mt-6 text-charcoal/65 max-w-2xl mx-auto">From a single retail unit to a hundred-store rollout — Stenzia is built to scale with you.</p>
          <Link to="/contact" data-testid="about-cta-btn" className="mt-8 inline-flex btn-primary">Request a Quote <ArrowUpRight size={18} /></Link>
        </div>
      </section>
    </div>
  );
}
